import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';

import { getFirstName } from '@/components/team/member-avatar';
import { SheetStepFooter, TeamSheet } from '@/components/team/team-sheet';
import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { SkeletonCard } from '@/components/ui/skeleton';
import { useServicesOfMany } from '@/hooks/use-services';
import { useHandover, useMemberFutureAppointments, useTeamMembers } from '@/hooks/use-team';
import { useTerms } from '@/hooks/use-terms';
import { DisabledMemberAppointment, HandoverItem, TeamMember } from '@/types/organization';
import { Service } from '@/types/service';
import { getApiErrorMessage } from '@/utils/api-error';
import { cn } from '@/utils/cn';
import { formatAppointmentWhen } from '@/utils/format-appointment';

const CANCEL = 'CANCEL';

/** Para onde vai um agendamento: o providerId de um colega, ou cancelar. */
type Destination = string;

interface RowChoice {
  to: Destination;
  /** Serviço escolhido à mão; sem ele vale a cópia achada pelo `catalogItemId`. */
  serviceId: string | null;
}

/** A cópia do mesmo serviço na agenda do colega (mesmo `catalogItemId`). */
export function matchServiceByCatalog(
  appointment: Pick<DisabledMemberAppointment, 'catalogItemId'>,
  services: Service[] | undefined,
): string | null {
  if (!appointment.catalogItemId || !services) return null;
  return (
    services.find((s) => s.isActive && s.catalogItemId === appointment.catalogItemId)?.id ?? null
  );
}

function DestinationPill({
  label,
  color,
  selected,
  danger,
  onPress,
}: {
  label: string;
  color?: string | null;
  selected: boolean;
  danger?: boolean;
  onPress: () => void;
}) {
  return (
    <PressableScale
      haptic
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      className={cn(
        'h-9 flex-row items-center gap-1.5 rounded-full px-3.5',
        selected ? (danger ? 'bg-error-600' : 'bg-primary') : 'bg-neutral-100',
      )}
      style={{ borderCurve: 'continuous' }}
    >
      {color && !selected ? (
        <View className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      ) : null}
      <ThemedText
        numberOfLines={1}
        className={cn('font-semibold text-[13px]', selected ? 'text-white' : 'text-neutral-700')}
      >
        {label}
      </ThemedText>
    </PressableScale>
  );
}

/**
 * Agendamentos futuros de quem foi desativado: cada um passa para um colega no
 * mesmo horário (o serviço vem da cópia com o mesmo `catalogItemId`; sem cópia, o
 * dono escolhe) ou é cancelado com aviso ao cliente. Nada vem marcado, e o
 * que a API recusar (horário ocupado) fica na lista com o motivo.
 */
export function HandoverSheet({
  open,
  onClose,
  member,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  member: TeamMember;
  onDone: (summary: { transferred: number; cancelled: number }) => void;
}) {
  const terms = useTerms();
  const { data: appointments, isLoading } = useMemberFutureAppointments(member.id, {
    enabled: open,
  });
  const { data: members } = useTeamMembers({ enabled: open });
  const handover = useHandover();

  const targets = useMemo(
    () => (members ?? []).filter((m) => m.id !== member.id && m.status === 'ACTIVE' && m.attends),
    [members, member.id],
  );
  const servicesBy = useServicesOfMany(open ? targets.map((t) => t.providerId) : []);

  const [choices, setChoices] = useState<Record<string, RowChoice>>({});
  const [failures, setFailures] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setChoices({});
    setFailures({});
    setError(null);
  }, [open]);

  const list = appointments ?? [];
  const firstName = getFirstName(member.name);
  const count = (n: number) => `${n} ${n === 1 ? terms.appointment : terms.appointmentPlural}`;

  const serviceFor = (a: DisabledMemberAppointment, choice: RowChoice | undefined) => {
    if (!choice || choice.to === CANCEL) return null;
    return choice.serviceId ?? matchServiceByCatalog(a, servicesBy[choice.to]);
  };

  const choose = (ids: string[], to: Destination) => {
    setChoices((prev) => {
      const next = { ...prev };
      for (const id of ids) next[id] = { to, serviceId: null };
      return next;
    });
    setFailures((prev) => {
      const next = { ...prev };
      for (const id of ids) delete next[id];
      return next;
    });
    setError(null);
  };

  const pickService = (appointmentId: string, serviceId: string) => {
    setChoices((prev) => ({ ...prev, [appointmentId]: { ...prev[appointmentId], serviceId } }));
    setError(null);
  };

  const submit = async () => {
    const missing = list.filter((a) => {
      const choice = choices[a.id];
      if (!choice) return true;
      return choice.to !== CANCEL && !serviceFor(a, choice);
    });
    if (missing.length > 0) {
      setError(`Falta o destino de ${count(missing.length)}.`);
      return;
    }

    const items: HandoverItem[] = list.map((a) => {
      const choice = choices[a.id];
      return choice.to === CANCEL
        ? { appointmentId: a.id, action: 'CANCEL' }
        : {
            appointmentId: a.id,
            action: 'TRANSFER',
            toProviderId: choice.to,
            serviceId: serviceFor(a, choice) as string,
          };
    });

    try {
      const result = await handover.mutateAsync({ memberId: member.id, items });
      if (result.failed.length === 0) {
        onDone({ transferred: result.transferred.length, cancelled: result.cancelled.length });
        return;
      }
      // O que não passou volta sem destino, com o motivo embaixo.
      setFailures(Object.fromEntries(result.failed.map((f) => [f.appointmentId, f.message])));
      setChoices((prev) => {
        const next = { ...prev };
        for (const f of result.failed) delete next[f.appointmentId];
        return next;
      });
      const n = result.failed.length;
      setError(`${count(n)} não ${n === 1 ? 'passou' : 'passaram'}. Escolha outro destino.`);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível repassar agora.'));
    }
  };

  const allTo = (to: Destination) => list.length > 0 && list.every((a) => choices[a.id]?.to === to);

  return (
    <TeamSheet
      open={open}
      onClose={onClose}
      busy={handover.isPending}
      title={`${terms.title.appointmentPlural} de ${firstName}`}
      subtitle={`${firstName} saiu da equipe. Passe cada um para alguém ou cancele.`}
      footer={
        list.length > 0 ? (
          <SheetStepFooter
            onBack={onClose}
            backLabel="Depois"
            onNext={() => void submit()}
            nextLabel="Confirmar"
            busy={handover.isPending}
            error={error}
          />
        ) : !isLoading ? (
          <SheetStepFooter onNext={onClose} nextLabel="Fechar" />
        ) : null
      }
    >
      {isLoading ? (
        <View className="gap-3">
          <SkeletonCard showHeader={false} lines={2} />
          <SkeletonCard showHeader={false} lines={2} />
        </View>
      ) : list.length === 0 ? (
        <ThemedText className="py-2 text-sm text-neutral-500">
          {`Nenhum ${terms.appointment} futuro na agenda de ${firstName}.`}
        </ThemedText>
      ) : (
        <View>
          {list.length > 1 ? (
            <View className="gap-2.5 pb-3">
              <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
                Todos para
              </ThemedText>
              <View className="flex-row flex-wrap gap-2">
                {targets.map((t) => (
                  <DestinationPill
                    key={t.providerId}
                    label={getFirstName(t.name)}
                    color={t.color}
                    selected={allTo(t.providerId)}
                    onPress={() =>
                      choose(
                        list.map((a) => a.id),
                        t.providerId,
                      )
                    }
                  />
                ))}
                <DestinationPill
                  label="Cancelar todos"
                  danger
                  selected={allTo(CANCEL)}
                  onPress={() =>
                    choose(
                      list.map((a) => a.id),
                      CANCEL,
                    )
                  }
                />
              </View>
            </View>
          ) : null}

          {list.map((a) => {
            const choice = choices[a.id];
            const target = targets.find((t) => t.providerId === choice?.to);
            const services = target ? servicesBy[target.providerId] : undefined;
            const matched = matchServiceByCatalog(a, services);
            const failure = failures[a.id];

            return (
              <View key={a.id} className="gap-2.5 border-t border-neutral-100 py-3.5">
                <View className="gap-0.5">
                  <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
                    {formatAppointmentWhen(a.startsAt)}
                  </ThemedText>
                  <ThemedText numberOfLines={1} className="text-sm text-neutral-500">
                    {`${a.clientName} · ${a.serviceName}`}
                  </ThemedText>
                </View>

                <View className="flex-row flex-wrap gap-2">
                  {targets.map((t) => (
                    <DestinationPill
                      key={t.providerId}
                      label={getFirstName(t.name)}
                      color={t.color}
                      selected={choice?.to === t.providerId}
                      onPress={() => choose([a.id], t.providerId)}
                    />
                  ))}
                  <DestinationPill
                    label="Cancelar"
                    danger
                    selected={choice?.to === CANCEL}
                    onPress={() => choose([a.id], CANCEL)}
                  />
                </View>

                {failure ? (
                  <ThemedText className="text-xs leading-4 text-error-600">{failure}</ThemedText>
                ) : choice?.to === CANCEL ? (
                  <ThemedText className="text-xs leading-4 text-neutral-500">
                    {`${terms.agree('O', 'client')} ${terms.client} recebe o aviso do cancelamento.`}
                  </ThemedText>
                ) : target && !services ? (
                  <ThemedText className="text-xs leading-4 text-neutral-400">
                    Carregando serviços…
                  </ThemedText>
                ) : target && matched && !choice?.serviceId ? (
                  <View className="flex-row items-center gap-1.5">
                    <Ionicons className="text-success-600" name="checkmark-circle" size={14} />
                    <ThemedText className="text-xs leading-4 text-neutral-500">
                      {`Mesmo serviço, na agenda de ${getFirstName(target.name)}`}
                    </ThemedText>
                  </View>
                ) : target && services ? (
                  <ServicePicker
                    firstName={getFirstName(target.name)}
                    services={services.filter((s) => s.isActive)}
                    selectedId={choice?.serviceId ?? null}
                    onPick={(serviceId) => pickService(a.id, serviceId)}
                  />
                ) : null}
              </View>
            );
          })}
        </View>
      )}
    </TeamSheet>
  );
}

/** Sem cópia do serviço no colega: o dono escolhe um dos serviços dele. */
function ServicePicker({
  firstName,
  services,
  selectedId,
  onPick,
}: {
  firstName: string;
  services: Service[];
  selectedId: string | null;
  onPick: (serviceId: string) => void;
}) {
  if (services.length === 0) {
    return (
      <ThemedText className="text-xs leading-4 text-error-600">
        {`${firstName} não tem serviço ativo. Escolha outra pessoa ou cancele.`}
      </ThemedText>
    );
  }
  return (
    <View className="gap-2">
      <ThemedText className="text-xs leading-4 text-neutral-500">
        {`Qual serviço de ${firstName}?`}
      </ThemedText>
      <View className="flex-row flex-wrap gap-2">
        {services.map((s) => (
          <DestinationPill
            key={s.id}
            label={s.name}
            selected={selectedId === s.id}
            onPress={() => onPick(s.id)}
          />
        ))}
      </View>
    </View>
  );
}
